import React, { useCallback, useRef, useState } from 'react';
import { Box, Flex, HStack, VStack, Text, Spinner, IconButton, useToast } from '@chakra-ui/react';
import { FiUploadCloud, FiTrash2, FiEye, FiDownload, FiPaperclip } from 'react-icons/fi';
import api, { attachmentKind, downloadAttachment } from '../../services/api';
import {
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS,
  formatFileSize,
  iconForAttachment,
  isAcceptedAttachmentType,
} from './attachmentMeta';
import EventAttachmentPreviewModal from './EventAttachmentPreviewModal';

/**
 * Multi-file uploader for event compliance materials (decks, pamphlets, PDFs,
 * images). Mirrors EventCoverUploader's upload pattern: each file POSTs to
 * /api/event-attachments and the returned ref ({ id, url, name, contentType,
 * size }) is appended to `value`. The parent stores only these refs on the
 * event — this component never holds bytes after upload.
 */
const EventAttachmentUploader = ({ value, onChange }) => {
  const toast = useToast();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(null);

  const items = Array.isArray(value) ? value : [];

  const warn = (description) =>
    toast({ title: "Couldn't add file", description, status: 'warning', duration: 3200, isClosable: true });

  const uploadOne = useCallback(async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await api.post('/api/event-attachments', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data; // { id, url, name, contentType, size }
  }, []);

  const handleFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []);
      if (!files.length) return;

      const room = MAX_ATTACHMENTS - items.length;
      if (room <= 0) {
        warn(`You can attach up to ${MAX_ATTACHMENTS} files.`);
        return;
      }

      const accepted = [];
      for (const file of files.slice(0, room)) {
        if (!isAcceptedAttachmentType(file)) {
          warn(`"${file.name}" isn’t a supported type. Use PDF, PowerPoint, PNG, or JPG.`);
          continue;
        }
        if (file.size > MAX_ATTACHMENT_BYTES) {
          warn(`"${file.name}" is larger than 15MB.`);
          continue;
        }
        accepted.push(file);
      }
      if (files.length > room) {
        warn(`Only the first ${room} file${room === 1 ? '' : 's'} were added (max ${MAX_ATTACHMENTS}).`);
      }
      if (!accepted.length) return;

      setUploading(true);
      try {
        const uploaded = [];
        for (const file of accepted) {
          try {
            uploaded.push(await uploadOne(file));
          } catch (err) {
            warn(err?.response?.data?.detail || `Couldn’t upload "${file.name}".`);
          }
        }
        if (uploaded.length) onChange?.([...items, ...uploaded]);
      } finally {
        setUploading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, onChange, uploadOne],
  );

  const onInputChange = (e) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };
  const openPicker = () => !uploading && inputRef.current?.click();
  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (!uploading) handleFiles(e.dataTransfer.files);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    if (!uploading) setDragActive(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const removeItem = (id) => onChange?.(items.filter((it) => it.id !== id));

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        onChange={onInputChange}
        style={{ display: 'none' }}
      />

      {/* Dropzone */}
      <Flex
        role="button"
        onClick={openPicker}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        direction="column"
        align="center"
        justify="center"
        gap={2}
        px={4}
        py={6}
        textAlign="center"
        borderRadius="12px"
        border="1px dashed"
        borderColor={dragActive ? 'rgba(99,179,237,0.55)' : 'rgba(255,255,255,0.14)'}
        bg={dragActive ? 'rgba(99,179,237,0.06)' : '#171717'}
        cursor={uploading ? 'default' : 'pointer'}
        transition="all 0.15s"
        _hover={{ borderColor: uploading ? undefined : 'rgba(255,255,255,0.3)' }}
      >
        <Flex
          w="40px"
          h="40px"
          borderRadius="full"
          bg="rgba(255,255,255,0.06)"
          border="1px solid rgba(255,255,255,0.12)"
          align="center"
          justify="center"
          color="rgba(255,255,255,0.6)"
        >
          {uploading ? <Spinner size="sm" /> : dragActive ? <FiUploadCloud size={18} /> : <FiPaperclip size={17} />}
        </Flex>
        <Text fontSize="sm" fontWeight="600" color="#fafafa">
          {uploading ? 'Uploading…' : dragActive ? 'Drop files to upload' : 'Add event materials'}
        </Text>
        <Text fontSize="xs" color="rgba(255,255,255,0.4)" lineHeight="1.6">
          Drag & drop or click to upload · PDF, PPT, PPTX, PNG, JPG · up to 15MB each
        </Text>
      </Flex>

      {/* Uploaded files */}
      {items.length > 0 && (
        <VStack align="stretch" spacing={2} mt={3}>
          {items.map((item) => {
            const Icon = iconForAttachment(item.contentType, item.name);
            const kind = attachmentKind(item.contentType, item.name);
            const previewable = kind === 'image' || kind === 'pdf';
            return (
              <HStack
                key={item.id}
                spacing={3}
                px={3}
                py={2.5}
                borderRadius="10px"
                bg="rgba(255,255,255,0.03)"
                border="1px solid rgba(255,255,255,0.08)"
              >
                <Box color="rgba(255,255,255,0.55)" flexShrink={0} display="flex" alignItems="center">
                  <Icon size={16} />
                </Box>
                <Box flex={1} minW={0}>
                  <Text fontSize="sm" color="#fafafa" noOfLines={1}>
                    {item.name}
                  </Text>
                  {item.size ? (
                    <Text fontSize="xs" color="rgba(255,255,255,0.4)">
                      {formatFileSize(item.size)}
                    </Text>
                  ) : null}
                </Box>
                <HStack spacing={1} flexShrink={0}>
                  <IconButton
                    aria-label={previewable ? 'Preview file' : 'Download file'}
                    icon={previewable ? <FiEye size={15} /> : <FiDownload size={15} />}
                    size="sm"
                    variant="ghost"
                    color="rgba(255,255,255,0.55)"
                    _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                    _focus={{ boxShadow: 'none' }}
                    onClick={() =>
                      previewable ? setPreview(item) : downloadAttachment(item.url, item.name)
                    }
                  />
                  <IconButton
                    aria-label="Remove file"
                    icon={<FiTrash2 size={15} />}
                    size="sm"
                    variant="ghost"
                    color="rgba(255,255,255,0.45)"
                    _hover={{ color: 'rgba(248,113,113,0.9)', bg: 'rgba(239,68,68,0.08)' }}
                    _focus={{ boxShadow: 'none' }}
                    onClick={() => removeItem(item.id)}
                  />
                </HStack>
              </HStack>
            );
          })}
        </VStack>
      )}

      <EventAttachmentPreviewModal
        isOpen={!!preview}
        attachment={preview}
        onClose={() => setPreview(null)}
      />
    </Box>
  );
};

export default EventAttachmentUploader;
